import { Link, getRouteApi, useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { RealmInfo } from '@realmery/realm-core';
import { RealmClientError, onRealmsChanged, onSyncChange, realms } from '../db/client.ts';
import {
  realmKey,
  type BackupUsage,
  type DeletedRealm,
  type DuplicateChoice,
  type RealmSummary,
  type RealmVersion,
  type SharedCopy,
  type SyncInfo,
} from '../db/protocol.ts';
import { backupState, dismissSafariNote, recordExport, safariMayClearStorage, safariNoteDismissed, snoozeReminder } from '../backups.ts';
import { SAMPLE_REALM_PATH } from '../sampleRealm.ts';
import { START_CHOSEN_REALM, START_LAST_REALM, claimStart, lastRealm, settings } from '../settings.ts';
import { formatDate, isStoragePersistent, requestPersistentStorage, saveBytes } from '../files.ts';
import { AccountError } from '../account/api.ts';
import { FingerprintCheck, useChecks, type Checks } from './FingerprintCheck.tsx';
import { accept, fetchShared, leave, roleLabel, sharedCopies, sharedRealms, sharedWithMe, type Share } from '../account/sharing.ts';

const route = getRouteApi('/_app/');

type Notice = { kind: 'info' | 'error'; text: string } | null;

function errorText(error: unknown): string {
  return error instanceof RealmClientError ? error.message : 'Something went wrong. Please try again.';
}

/** Each realm's backup state (db/sync.ts), by realm file, kept current by the worker's events. */
function useSyncStates(): Record<string, SyncInfo> {
  const [states, setStates] = useState<Record<string, SyncInfo>>({});
  useEffect(() => {
    let live = true;
    const stop = onSyncChange((file, info) => setStates((known) => ({ ...known, [file]: info })));
    // Events that arrived first are newer than this answer.
    void realms.syncStates().then((all) => live && setStates((known) => ({ ...all, ...known })), () => undefined);
    return () => {
      live = false;
      stop();
    };
  }, []);
  return states;
}

/** "340 KB", "12.3 MB", "1 GB". */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${+(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${+(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** What a realm card says about its backup (docs/SYNC-PLAN.md section 4.5). */
export function syncText(info: SyncInfo | undefined): { text: string; problem: boolean } | null {
  switch (info?.status) {
    case undefined:
      return null;
    case 'synced':
      return { text: info.backedUpAt ? `Backed up ${formatDate(info.backedUpAt)}` : 'Backed up', problem: false };
    case 'pending':
      return { text: 'Waiting to back up…', problem: false };
    case 'uploading':
      return { text: 'Backing up…', problem: false };
    case 'offline':
      return { text: 'Offline: backs up when you’re connected again', problem: false };
    case 'remote':
      return { text: 'Backed up from another device: downloads when you open it', problem: false };
    case 'downloading':
      return { text: 'Downloading…', problem: false };
    case 'conflict':
      return { text: 'Not backed up: another device changed this realm first. Sorting that out is coming soon.', problem: true };
    case 'deleted-elsewhere':
      return { text: 'Not backed up: this realm was deleted on another device.', problem: true };
    case 'full':
      return { text: 'Not backed up: your account’s backup space is full.', problem: true };
    case 'too-large':
      return { text: 'Not backed up: this realm is larger than your account’s backup space.', problem: true };
    case 'unavailable':
      return { text: 'Backup isn’t available right now. It will try again.', problem: true };
    case 'signed-out':
      return { text: 'Not backed up: sign in again to back up.', problem: true };
    case 'error':
      return { text: 'Couldn’t back up. It will try again.', problem: true };
    case 'off':
      return { text: 'Not backed up: backing up is off for this realm on this device.', problem: false };
  }
}

export function RealmsPage() {
  const list = route.useLoaderData();
  const router = useRouter();
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  const [persistent, setPersistent] = useState<boolean | null>(null);
  const [duplicate, setDuplicate] = useState<{ bytes: Uint8Array; existing: RealmSummary; incoming: RealmInfo } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void isStoragePersistent().then(setPersistent);
  }, []);

  // Settings > When Realmery starts: only when this page is where the app began.
  useEffect(() => {
    if (!claimStart()) return;
    const s = settings();
    const target = s['general.onStart'] === START_LAST_REALM ? lastRealm() : s['general.onStart'] === START_CHOSEN_REALM ? s['general.startRealm'] : null;
    if (target && list.some((realm) => realmKey(realm.file) === target)) {
      void router.navigate({ to: '/realm/$realmKey', params: { realmKey: target }, replace: true });
    }
    // Once, as the page first shows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Another device's changes: listed again when they arrive, and when the page comes back to the front.
  useEffect(() => {
    const refresh = () => void router.invalidate();
    const visible = () => document.visibilityState === 'visible' && refresh();
    const stop = onRealmsChanged(refresh);
    document.addEventListener('visibilitychange', visible);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', visible);
    };
  }, [router]);

  async function run(work: () => Promise<string | void>) {
    setBusy(true);
    setNotice(null);
    try {
      const text = await work();
      if (text) setNotice({ kind: 'info', text });
      await router.invalidate();
    } catch (error) {
      setNotice({ kind: 'error', text: errorText(error) });
    } finally {
      setBusy(false);
    }
  }

  async function keepStorage() {
    setPersistent(await requestPersistentStorage());
  }

  function createRealm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    void run(async () => {
      const realm = await realms.createRealm(String(data.get('name') ?? ''), String(data.get('description') ?? ''));
      form.reset();
      void keepStorage();
      await router.navigate({ to: '/realm/$realmKey', params: { realmKey: realmKey(realm.file) } });
    });
  }

  async function importBytes(bytes: Uint8Array, choice?: DuplicateChoice) {
    const outcome = await realms.importRealm(bytes, choice);
    if (outcome.status === 'duplicate') {
      setDuplicate({ bytes, existing: outcome.existing, incoming: outcome.incoming });
      return;
    }
    setDuplicate(null);
    void keepStorage();
    const upgraded = outcome.upgradedFrom < outcome.realm.schemaVersion
      ? ` It was upgraded from realm format ${outcome.upgradedFrom} to ${outcome.realm.schemaVersion}.`
      : '';
    return `Imported “${outcome.realm.name}”.${upgraded}`;
  }

  /** The desktop's sample realm, imported like any file; if it's here already, the usual choice to replace it or keep both. */
  function addSampleRealm() {
    void run(async () => {
      const response = await fetch(`/${SAMPLE_REALM_PATH}`);
      if (!response.ok) throw new RealmClientError('The sample realm could not be fetched. Check the connection and try again.');
      return importBytes(new Uint8Array(await response.arrayBuffer()));
    });
  }

  function onFileChosen(file: File | undefined) {
    if (!file) return;
    void run(async () => importBytes(new Uint8Array(await file.arrayBuffer())));
    if (fileInput.current) fileInput.current.value = '';
  }

  function exportRealm(realm: RealmSummary) {
    void run(async () => {
      const { fileName, bytes } = await realms.exportRealm(realm.file);
      saveBytes(bytes as Uint8Array<ArrayBuffer>, fileName);
      recordExport(realmKey(realm.file));
      return `Saved “${fileName}”. It opens in Realmery on the desktop too.`;
    });
  }

  // Re-read after an export or a "remind me later" (both change only this browser's local storage).
  const [, reread] = useState(0);
  const syncStates = useSyncStates();
  // A realm the account has a backup of needs no reminder to export it: backed up from
  // here, or from another device and not downloaded here yet.
  const onServer = (realm: RealmSummary) => ['synced', 'remote', 'downloading'].includes(syncStates[realm.file]?.status ?? '');
  const exportDue = (realm: RealmSummary) => !onServer(realm) && backupState(realm, realmKey(realm.file)).due;
  const due = list.filter(exportDue);
  const [safariNote, setSafariNote] = useState(() => safariMayClearStorage() && !safariNoteDismissed());
  const [usage, setUsage] = useState<BackupUsage | null>(null);
  useEffect(() => {
    void realms.backupUsage().then(setUsage, () => undefined);
  }, [list]);
  // How many people each realm is shared with; none shown when offline.
  const [shared, setShared] = useState<ReadonlyMap<string, number>>(new Map());
  useEffect(() => {
    void sharedRealms().then(setShared, () => undefined);
  }, [list]);

  return (
    <div className="realms-page">
      <section className="intro">
        <h1>Your realms</h1>
        <p>
          Your realms are kept in this browser's private storage, encrypted with your account's key, and backed up to your
          account in that encrypted form, which the server can't read, so they appear on every device you sign in on.
          Export a realm to save a <code>.realmery</code> file for Realmery on the desktop or a backup of your own.
          Exported files are not encrypted.
        </p>
        {usage && (
          <p className="meta backup-usage">
            Backups use {formatBytes(usage.used)} of your {formatBytes(usage.quota)}.
          </p>
        )}
        {due.length > 0 && (
          <p className="callout backup-callout">
            {due.length === 1 ? '1 realm isn’t' : `${due.length} realms aren’t`} backed up yet. Export {due.length === 1 ? 'it' : 'them'}{' '}
            below to keep a copy outside this browser.
          </p>
        )}
        {safariNote && (
          <p className="callout">
            On an iPhone or iPad, Safari can clear a site’s storage after about a week without a visit. To keep your realms,
            add Realmery to your Home Screen (Share, then Add to Home Screen) and use it from there. The Home Screen app has
            storage of its own: export your realms here first, then sign in there and import them.{' '}
            <button
              type="button"
              className="link-button"
              onClick={() => {
                dismissSafariNote();
                setSafariNote(false);
              }}
            >
              Got it
            </button>
          </p>
        )}
        {persistent === false && (
          <p className="callout">
            This browser may clear stored realms when space runs low.{' '}
            <button type="button" className="link-button" onClick={() => void keepStorage()}>
              Ask to keep them
            </button>
            .
          </p>
        )}
      </section>

      {notice && (
        <p className={notice.kind === 'error' ? 'notice notice-error' : 'notice'} role={notice.kind === 'error' ? 'alert' : 'status'}>
          {notice.text}
        </p>
      )}

      {duplicate && (
        <section className="panel duplicate" role="alertdialog" aria-labelledby="duplicate-title">
          <h2 id="duplicate-title">“{duplicate.incoming.name}” is already here</h2>
          <p>
            This file is a copy of “{duplicate.existing.name}” (last edited {formatDate(duplicate.existing.modified)}). The file
            you chose was last edited {formatDate(duplicate.incoming.modified)}. Copies cannot be merged.
          </p>
          <div className="actions">
            <button type="button" className="danger" disabled={busy} onClick={() => void run(() => importBytes(duplicate.bytes, 'replace'))}>
              Replace the one here
            </button>
            <button type="button" disabled={busy} onClick={() => void run(() => importBytes(duplicate.bytes, 'copy'))}>
              Keep both
            </button>
            <button type="button" className="quiet" disabled={busy} onClick={() => setDuplicate(null)}>
              Cancel
            </button>
          </div>
        </section>
      )}

      <div className="realms-grid">
        <form className="panel new-realm" onSubmit={createRealm}>
          <h2>New realm</h2>
          <label>
            Name
            <input name="name" required maxLength={200} autoComplete="off" placeholder="The Shattered Coast" />
          </label>
          <label>
            Description <span className="optional">(optional)</span>
            <textarea name="description" rows={3} maxLength={2000} />
          </label>
          <div className="actions">
            <button type="submit" className="primary" disabled={busy}>
              Create realm
            </button>
            <button type="button" disabled={busy} onClick={() => fileInput.current?.click()}>
              Import .realmery file…
            </button>
            <input
              ref={fileInput}
              type="file"
              accept=".realmery"
              hidden
              onChange={(event) => onFileChosen(event.currentTarget.files?.[0])}
            />
          </div>
          <p className="meta">
            New to Realmery?{' '}
            <button type="button" className="link-button" disabled={busy} onClick={addSampleRealm}>
              Add the sample realm
            </button>
            , The Kingdom of Asteria, to explore.
          </p>
          <p className="meta">
            On Windows? <Link to="/download">Get the desktop app</Link>, which does more and works offline.
          </p>
        </form>

        {list.length === 0 && (
          <section className="panel empty">
            <p className="empty-glyph" aria-hidden="true">🗺️</p>
            <p>No realms yet. Create one, or import a realm file from Realmery on the desktop.</p>
          </section>
        )}

        {list.map((realm) => (
          <RealmCard key={realm.file} realm={realm} sharedWith={shared.get(realmKey(realm.file)) ?? 0} sync={syncStates[realm.file]} exportDue={exportDue(realm)} busy={busy} onRun={(work) => void run(work)} onExport={exportRealm} onSnooze={() => reread((n) => n + 1)} onDelete={(r) => void run(async () => {
            const restorable = isRestorable(syncStates[r.file]);
            await realms.deleteRealm(r.file);
            return restorable ? `Deleted “${r.name}”. You can restore it from Recently deleted for 30 days.` : `Deleted “${r.name}”.`;
          })} />
        ))}
      </div>

      <SharedWithMe />

      <RecentlyDeleted list={list} busy={busy} onRun={(work) => void run(work)} />
    </div>
  );
}

/** Whether deleting a realm here leaves it restorable from Recently deleted: the server has it. */
function isRestorable(info: SyncInfo | undefined): boolean {
  return !!info && info.status !== 'off' && (info.status === 'remote' || info.backedUpAt !== null);
}

type Run = (work: () => Promise<string | void>) => void;

function RealmCard({ realm, sharedWith, sync, exportDue, busy, onRun, onExport, onSnooze, onDelete }: {
  realm: RealmSummary;
  sharedWith: number;
  sync: SyncInfo | undefined;
  exportDue: boolean;
  busy: boolean;
  onRun: Run;
  onExport: (realm: RealmSummary) => void;
  onSnooze: () => void;
  onDelete: (realm: RealmSummary) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  const id = realmKey(realm.file);
  const backup = backupState(realm, id);
  const syncLine = syncText(sync);
  return (
    <article className="panel realm-card">
      <h2>
        <Link to="/realm/$realmKey" params={{ realmKey: id }}>
          {realm.name}
        </Link>
      </h2>
      {realm.description && <p className="realm-description">{realm.description}</p>}
      <p className="meta">
        {realm.entryCount} {realm.entryCount === 1 ? 'entry' : 'entries'} · edited {formatDate(realm.modified)}
        {backup.exportedAt && !exportDue && ` · exported ${formatDate(backup.exportedAt)}`}
        {sharedWith > 0 && (
          <>
            {' · '}
            <Link to="/realm/$realmKey/sharing" params={{ realmKey: id }}>
              shared with {sharedWith} {sharedWith === 1 ? 'person' : 'people'}
            </Link>
          </>
        )}
      </p>
      {syncLine && (
        <p className={syncLine.problem ? 'sync-status sync-problem' : 'sync-status'} role="status">
          {syncLine.text}
        </p>
      )}
      {sync?.copy && (
        <p className="backup-due copy-note" role="note">
          {sync.copy === 'conflict'
            ? 'Kept from a conflict: another device changed this realm while it had changes here, so this device’s version was kept as this copy. Compare it with the original and delete the one you don’t want.'
            : 'Kept from a deletion: another device deleted this realm while it had changes here, so they were kept as this copy.'}{' '}
          <button type="button" className="link-button" onClick={() => onRun(() => realms.dismissCopyNote(realm.file))}>
            Got it
          </button>
        </p>
      )}
      {exportDue && (
        <p className="backup-due" role="note">
          {backup.exportedAt ? `Changed since you exported it on ${formatDate(backup.exportedAt)}.` : 'Never exported from this browser.'}{' '}
          <button type="button" className="link-button" disabled={busy} onClick={() => onExport(realm)}>
            Export now
          </button>{' '}
          ·{' '}
          <button
            type="button"
            className="link-button"
            onClick={() => {
              snoozeReminder(id);
              onSnooze();
            }}
          >
            Remind me in 14 days
          </button>
        </p>
      )}
      <div className="actions">
        <Link className="button primary" to="/realm/$realmKey" params={{ realmKey: realmKey(realm.file) }}>
          Open
        </Link>
        <button type="button" disabled={busy} onClick={() => onExport(realm)}>
          Export
        </button>
        <button type="button" aria-expanded={backupOpen} onClick={() => setBackupOpen((open) => !open)}>
          Backup…
        </button>
        {confirming ? (
          <>
            <button type="button" className="danger" disabled={busy} onClick={() => onDelete(realm)}>
              {isRestorable(sync) ? 'Delete' : 'Delete for good'}
            </button>
            <button type="button" className="quiet" onClick={() => setConfirming(false)}>
              Keep
            </button>
          </>
        ) : (
          <button type="button" className="quiet" disabled={busy} onClick={() => setConfirming(true)}>
            Delete…
          </button>
        )}
      </div>
      {backupOpen && <BackupPanel realm={realm} sync={sync} busy={busy} onRun={onRun} onClose={() => setBackupOpen(false)} />}
    </article>
  );
}

/** A realm's backup: the switch for this device, and the versions the server keeps (docs/SYNC-PLAN.md 4.5). */
function BackupPanel({ realm, sync, busy, onRun, onClose }: {
  realm: RealmSummary;
  sync: SyncInfo | undefined;
  busy: boolean;
  onRun: Run;
  onClose: () => void;
}) {
  const [versions, setVersions] = useState<readonly RealmVersion[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    realms.realmVersions(realm.file).then(
      (found) => live && setVersions(found),
      (error: unknown) => live && setFailed(errorText(error)),
    );
    return () => {
      live = false;
    };
  }, [realm.file, sync?.backedUpAt]);
  // Shown at once when changed, then kept in step with what the worker reports.
  const [on, setOn] = useState(sync?.status !== 'off');
  useEffect(() => setOn(sync?.status !== 'off'), [sync?.status]);
  return (
    <section className="backup-panel" aria-label={`Backup of ${realm.name}`}>
      <label className="check">
        <input
          type="checkbox"
          checked={on}
          disabled={busy || sync?.status === 'remote'}
          onChange={(event) => {
            const next = event.currentTarget.checked;
            setOn(next);
            onRun(async () => {
              await realms.setBackup(realm.file, next);
              return next ? `Backing up “${realm.name}” again.` : `“${realm.name}” isn’t backed up from this device any more. Its backup on the server stays.`;
            });
          }}
        />
        Back up this realm from this device
      </label>
      <h3>Versions kept</h3>
      <p className="meta">The last 10, and one a day for 30 days. Restoring one makes it current again; the current one is kept too.</p>
      {failed && <p className="notice notice-error">{failed}</p>}
      {!versions && !failed && <p className="meta">Loading…</p>}
      {versions?.length === 0 && <p className="meta">None yet.</p>}
      {versions && versions.length > 0 && (
        <ul className="version-list">
          {versions.map((v) => (
            <li key={v.version}>
              <span>
                {formatDate(v.createdAt)}
                <br />
                <small>
                  {v.device} · {formatBytes(v.size)}
                </small>
              </span>
              {v.current ? (
                <span className="chip">Current</span>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    onRun(async () => {
                      await realms.restoreVersion(realm.file, v.version);
                      onClose();
                      return `Restored “${realm.name}” as it was on ${formatDate(v.createdAt)}.`;
                    })
                  }
                >
                  Restore
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="actions">
        <button type="button" className="quiet" onClick={onClose}>
          Close
        </button>
      </div>
    </section>
  );
}

/** Realms deleted within 30 days, from the server, each restorable (docs/SYNC-PLAN.md 4.5). */
function RecentlyDeleted({ list, busy, onRun }: { list: readonly RealmSummary[]; busy: boolean; onRun: Run }) {
  const [deleted, setDeleted] = useState<readonly DeletedRealm[]>([]);
  useEffect(() => {
    let live = true;
    realms.deletedRealms().then(
      (found) => live && setDeleted(found),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [list]);
  if (deleted.length === 0) return null;
  const daysLeft = (deletedAt: string) => Math.max(0, 30 - Math.floor((Date.now() - Date.parse(deletedAt)) / 86_400_000));
  return (
    <details className="panel recently-deleted">
      <summary>Recently deleted ({deleted.length})</summary>
      <p className="meta">Deleted realms are kept on the server for 30 days, then removed for good.</p>
      <ul className="version-list">
        {deleted.map((realm) => (
          <li key={realm.file}>
            <span>
              <strong>{realm.name}</strong> · {realm.entryCount} {realm.entryCount === 1 ? 'entry' : 'entries'} · deleted{' '}
              {formatDate(realm.deletedAt)} · {daysLeft(realm.deletedAt)} days left
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                onRun(async () => {
                  await realms.restoreRealm(realm.file);
                  return `Restored “${realm.name}”.`;
                })
              }
            >
              Restore
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * Shared with me (docs/SHARING-PLAN.md section 3.3): invitations to accept or decline, and
 * realms others share with this account. Hidden when there are none, or when offline.
 */
function SharedWithMe() {
  const router = useRouter();
  const [shares, setShares] = useState<readonly Share[]>([]);
  /** Offline: the shared realms kept on this device, which open as they are. */
  const [offline, setOffline] = useState<readonly SharedCopy[]>([]);
  const [notice, setNotice] = useState<Notice>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const checks = useChecks();
  const load = () =>
    sharedWithMe().then(
      async (found) => {
        setShares(found);
        setOffline([]);
        // Copies of realms no longer shared with this account (left on another device, or removed) go.
        await sharedCopies(found).catch(() => undefined);
      },
      async (error: unknown) => {
        if (!(error instanceof AccountError)) console.warn('Could not list realms shared with you', error);
        setOffline(await sharedCopies().catch(() => []));
      },
    );
  useEffect(() => void load(), []);
  if (shares.length === 0 && offline.length === 0 && !notice) return null;

  function open(key: string, file: Promise<string>) {
    setNotice(null);
    setOpening(key);
    file.then(
      (f) => router.navigate({ to: '/realm/$realmKey', params: { realmKey: realmKey(f) } }),
      (error: unknown) => {
        setOpening(null);
        setNotice({ kind: 'error', text: error instanceof AccountError || error instanceof RealmClientError ? error.message : 'The realm couldn’t be opened.' });
      },
    );
  }

  async function act(work: () => Promise<void>, done: string) {
    setNotice(null);
    try {
      await work();
      setNotice({ kind: 'info', text: done });
      await load();
    } catch (error) {
      setNotice({ kind: 'error', text: error instanceof AccountError ? error.message : 'That didn’t work. Please try again.' });
    }
  }

  const invitations = shares.filter((s) => s.status === 'invited');
  const joined = shares.filter((s) => s.status === 'member');
  return (
    <section className="shared-with-me" aria-labelledby="shared-with-me-heading">
      <h2 id="shared-with-me-heading">Shared with me</h2>
      {notice && (
        <p className={notice.kind === 'error' ? 'notice notice-error' : 'notice'} role={notice.kind === 'error' ? 'alert' : 'status'}>
          {notice.text}
        </p>
      )}
      <div className="realms-grid">
        {invitations.map((share) => (
          <article key={`${share.owner.userId}/${share.realmId}`} className="panel realm-card invitation" aria-label={`Invitation from ${share.owner.email}`}>
            {share.name === null ? (
              <>
                <h3>An invitation that doesn’t check out</h3>
                <p className="notice-error">
                  This invitation from {share.owner.email} wasn’t sealed by their account, or was changed on the way. Decline it, and
                  ask them to invite you again.
                </p>
              </>
            ) : (
              <>
                <h3>{share.name}</h3>
                <p>
                  {share.owner.email} invited you as a {roleLabel(share.role)}.
                </p>
              </>
            )}
            <FingerprintCheck userId={share.owner.userId} fingerprint={share.owner.fingerprint} checks={checks} />
            <div className="actions">
              {share.name !== null && (
                <button type="button" className="primary" onClick={() => void act(() => accept(share), `Accepted. “${share.name}” is shared with you.`)}>
                  Accept
                </button>
              )}
              <button type="button" className="quiet" onClick={() => void act(() => leave(share), 'Declined the invitation.')}>
                Decline
              </button>
            </div>
          </article>
        ))}
        {joined.map((share) => (
          <SharedRealmCard
            key={`${share.owner.userId}/${share.realmId}`}
            share={share}
            checks={checks}
            opening={opening === `${share.owner.userId}/${share.realmId}`}
            onOpen={() => open(`${share.owner.userId}/${share.realmId}`, fetchShared(share))}
            onLeave={() => void act(() => leave(share), `You left “${share.name ?? 'the realm'}”.`)}
          />
        ))}
        {offline.map((copy) => (
          <article key={copy.file} className="panel realm-card shared-realm" aria-label={copy.name}>
            <h3>{copy.name}</h3>
            <p className="meta">
              Shared by {copy.ownerEmail} · you’re a {roleLabel(copy.role)} · the copy on this device
            </p>
            <div className="actions">
              <button type="button" className="primary" onClick={() => open(copy.file, Promise.resolve(copy.file))}>
                Open
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function SharedRealmCard({ share, checks, opening, onOpen, onLeave }: { share: Share; checks: Checks; opening: boolean; onOpen: () => void; onLeave: () => void }) {
  const [confirming, setConfirming] = useState(false);
  // A Co-owner opens the owner's realm itself; a Player or Reader, the copy made for them.
  const copy = share.role === 'editor' ? share.realm : share.edition;
  const readable = share.name !== null && copy !== null && share.key !== null;
  return (
    <article className="panel realm-card shared-realm" aria-label={share.name ?? `Shared by ${share.owner.email}`}>
      <h3>{share.name ?? 'A shared realm that doesn’t check out'}</h3>
      <p className="meta">
        Shared by {share.owner.email} · you’re a {roleLabel(share.role)}
        {copy && ` · updated ${formatDate(copy.updatedAt)}`}
      </p>
      {!readable && share.name !== null && <p className="meta">{share.owner.email} hasn’t sent a copy for you yet.</p>}
      <FingerprintCheck userId={share.owner.userId} fingerprint={share.owner.fingerprint} checks={checks} />
      <div className="actions">
        {readable && (
          <button type="button" className="primary" disabled={opening} onClick={onOpen}>
            {opening ? 'Opening…' : 'Open'}
          </button>
        )}
        {confirming ? (
          <>
            <button type="button" className="danger" onClick={onLeave}>
              Leave
            </button>
            <button type="button" className="quiet" onClick={() => setConfirming(false)}>
              Stay
            </button>
          </>
        ) : (
          <button type="button" className="quiet" onClick={() => setConfirming(true)}>
            Leave…
          </button>
        )}
      </div>
    </article>
  );
}
